/**
 * Cache-first strategy implementation
 *
 * This strategy:
 * 1. Tries to get the resource from the cache first
 * 2. If not in cache, fetches from network and caches the response
 * 3. Returns the cached or network response
 *
 * Best for: Static assets that rarely change (images, fonts)
 */
import { debugLog } from "../utils/cache-helpers";
import { generateCacheKey } from "../utils/hash-generator";

/**
 * Cache-first strategy for fetch requests
 * @param request The request to handle
 * @param cacheName The name of the cache to use
 * @returns Promise resolving to the response
 */
export async function cacheFirstStrategy(
  request: Request,
  cacheName: string
): Promise<Response> {
  const cacheKey = generateCacheKey(request.url);

  try {
    // Try to get from cache first
    const cache = await caches.open(cacheName);
    const cachedResponse = await cache.match(cacheKey);

    if (cachedResponse) {
      debugLog(`Cache hit for ${request.url}`);
      return cachedResponse;
    }

    // Not in cache, fetch from network
    debugLog(`Cache miss for ${request.url}, fetching from network`);
    const networkResponse = await fetch(request);

    // Cache the response if it's valid
    if (networkResponse.ok && networkResponse.status < 400) {
      // Clone the response before caching it
      const responseToCache = networkResponse.clone();
      cache.put(cacheKey, responseToCache);
      debugLog(`Cached response for ${request.url}`);
    }

    return networkResponse;
  } catch (error) {
    debugLog(`Error in cache-first strategy for ${request.url}:`, error);
    // If both cache and network fail, throw an error
    throw error;
  }
}

/**
 * Cache-first strategy for the Cache API
 * @param cache The cache object
 * @param url The URL to fetch
 * @param fetchOptions Options for the fetch request
 * @returns Promise resolving to the response
 */
export async function cacheFirstWithCacheAPI(
  cache: Cache,
  url: string,
  fetchOptions?: RequestInit
): Promise<Response> {
  const cacheKey = generateCacheKey(url);

  try {
    // Try to get from cache first
    const cachedResponse = await cache.match(cacheKey);

    if (cachedResponse) {
      debugLog(`Cache hit for ${url}`);
      return cachedResponse;
    }

    // Not in cache, fetch from network
    debugLog(`Cache miss for ${url}, fetching from network`);
    const networkResponse = await fetch(url, fetchOptions);

    // Cache the response if it's valid
    if (networkResponse.ok && networkResponse.status < 400) {
      // Clone the response before caching it
      const responseToCache = networkResponse.clone();
      cache.put(cacheKey, responseToCache);
      debugLog(`Cached response for ${url}`);
    }

    return networkResponse;
  } catch (error) {
    debugLog(`Error in cache-first strategy for ${url}:`, error);
    // If both cache and network fail, throw an error
    throw error;
  }
}
